import React, { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { FormattedNumberParts } from "react-intl";
import type { SelectedProducts } from "../../types/product";
import ProductManager from "../../lib/Product/ProductManager.ts";
import { createLogger } from "../../lib/logger.ts";
import { SolarPanel } from "./Icons/SolarPanel.tsx";
import { BatteryCapacity } from "./Icons/BatteryCapacity.tsx";

const logger = createLogger("SystemOverview");

type Props = {
    moduleCount: number;
    selectedProducts: SelectedProducts;
    kwPerYearAndModule: number;
    powerConsumption: number;
    costsPerKwh: number;
    address: AddressWithPrediction;
    pvSize: number;
};

type SystemOverviewItemProps = {
    icon: React.ReactNode;
    value: React.ReactNode;
};

const SystemOverviewItem: React.FC<SystemOverviewItemProps> = ({ icon, value }) => (
    <Stack spacing={0} alignItems="center" justifyContent="center">
        {icon}
        <Typography variant="h4">{value}</Typography>
    </Stack>
);

const SystemOverview: React.FC<Props> = ({
    moduleCount,
    selectedProducts,
    kwPerYearAndModule,
    powerConsumption,
    costsPerKwh,
    address,
    pvSize
}) => {
    const [summaryStore, setSummaryStore] = useState(
        ProductManager.getCostsCalculation(selectedProducts, moduleCount, kwPerYearAndModule, powerConsumption, costsPerKwh, address)
    );

    useEffect(() => {
        setSummaryStore(
            ProductManager.getCostsCalculation(selectedProducts, moduleCount, kwPerYearAndModule, powerConsumption, costsPerKwh, address)
        );

        logger.debug("Updated System Overview", { selectedProducts, moduleCount, pvSize });
    }, [selectedProducts, moduleCount, kwPerYearAndModule, powerConsumption, costsPerKwh, address, pvSize]);

    const storageSize = summaryStore.yield?.storageSize;

    return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={4} alignSelf={"center"}>
            <SystemOverviewItem
                icon={<SolarPanel/>}
                value={
                    <Typography variant={"h4"} fontFamily={'"Inter", sans-serif'} fontWeight={"bolder"}>
                        <FormattedNumberParts
                            value={pvSize}
                            minimumFractionDigits={3}
                            maximumFractionDigits={3}
                            children={parts => (
                                <>
                                    {parts[0].value}{parts[1].value}{parts[2].value.substring(0, 1)}
                                    <Typography
                                        variant={"h6"}
                                        fontWeight={"bolder"}
                                        component={"span"}
                                    >
                                        {parts[2].value.substring(1)}
                                    </Typography>
                                </>
                            )}/> kWp
                    </Typography>
                }
            />
            <SystemOverviewItem
                icon={<BatteryCapacity/>}
                value={
                    storageSize !== undefined ? (
                        <Typography variant={"h4"} fontFamily={'"Inter", sans-serif'} fontWeight={"bolder"}>
                            <FormattedNumberParts
                                value={storageSize}
                                minimumFractionDigits={2}
                                maximumFractionDigits={2}
                                children={parts => (
                                    <>
                                        {parts[0].value}{parts[1].value}{parts[2].value.substring(0, 1)}
                                        <Typography
                                            variant={"h6"}
                                            fontWeight={"bolder"}
                                            component={"span"}
                                        >
                                            {parts[2].value.substring(1)}
                                        </Typography>
                                    </>
                                )}/> kWh
                        </Typography>
                    ) : (
                        "Nicht ausgewählt"
                    )
                }
            />
        </Stack>
    );
};

export default SystemOverview;
